/**
 * MessageList Component - HYB Shop Chat
 * Scrollable message container with date dividers
 */

import { useMemo, useEffect } from 'react';
import { MessageSquare, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/responsive';
import { EmptyState } from '../../ui/EmptyState';
import { DateDivider } from '../shared';
import { TypingIndicator } from '../TypingIndicator';
import { MessageGroup } from './MessageGroup';
import { MessageStatus } from '../ReadReceipt';

interface Message {
  id: string;
  isAdmin: boolean;
  text?: string;
  images?: string[];
  type?: 'text' | 'image';
  createdAt?: string;
  status?: MessageStatus;
}

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  isUserTyping?: boolean;
  typingUserName?: string;
  messageEndRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
}

/**
 * Group messages by date for inserting date dividers
 */
function groupMessagesByDate(messages: Message[]): Map<string, Message[]> {
  const groups = new Map<string, Message[]>();

  messages.forEach((message) => {
    const date = message.createdAt
      ? new Date(message.createdAt).toDateString()
      : 'unknown';

    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(message);
  });

  return groups;
}

export function MessageList({
  messages,
  isLoading = false,
  isUserTyping = false,
  typingUserName,
  messageEndRef,
  className,
}: MessageListProps) {
  const messagesByDate = useMemo(() => groupMessagesByDate(messages), [messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messageEndRef?.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, messageEndRef]); // Depend on messages.length to trigger on new messages

  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto',
        'bg-stone-50 dark:bg-gray-900',
        'px-4 lg:px-6 py-4',
        className
      )}
      role="log"
      aria-live="polite"
      aria-label="ข้อความในสนทนา"
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center gap-2 text-sm text-stone-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          กำลังโหลดข้อความ...
        </div>
      ) : messages.length === 0 ? (
        <EmptyState
          icon={<MessageSquare className="h-5 w-5" />}
          title="ยังไม่มีข้อความในสนทนานี้"
          description="พิมพ์ข้อความด้านล่างเพื่อเริ่มต้นการสนทนา"
        />
      ) : (
        <div className="space-y-2">
          {Array.from(messagesByDate.entries()).map(([dateString, dayMessages]) => (
            <div key={dateString}>
              {/* Date Divider */}
              <DateDivider date={new Date(dateString)} />

              {/* Messages for this date */}
              <MessageGroup messages={dayMessages} />
            </div>
          ))}

          {/* Typing Indicator */}
          <TypingIndicator isVisible={isUserTyping} userName={typingUserName} />
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messageEndRef as any} />
    </div>
  );
}

export default MessageList;
